import React from "react";
import { Box, Typography } from "@mui/material";

import ElectricBoltOutlinedIcon from "@mui/icons-material/ElectricBoltOutlined";
import ReduceCapacityOutlinedIcon from "@mui/icons-material/ReduceCapacityOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import ShowChartOutlinedIcon from "@mui/icons-material/ShowChartOutlined";

import GradientIcon from "../../components/GradientIcon";

const gradientHoverSx = {
  position: "relative",
  border: "2px solid transparent",
  backgroundClip: "padding-box",
  transition: "all 0.3s ease",
  "&:hover::before": {
    opacity: 1,
  },
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    borderRadius: "24px",
    padding: "2px",
    background: "linear-gradient(90deg, #FF7FAC, #5834BA)",
    WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
    WebkitMaskComposite: "xor",
    maskComposite: "exclude",
    opacity: 0,
    transition: "opacity 0.3s ease",
  },
};

const RealImpact = () => {
  return (
    <Box
      sx={{
        pt: "80px",
        px: "84px",
        "@media (max-width: 1200px)": {
          px: "24px",
        },
        "@media (max-width: 780px)": {
          px: "16px",
        },
      }}
    >
      <Typography
        variant="h2"
        component="h2"
        sx={{
          fontSize: "3rem",
          fontWeight: "bold",
          textAlign: "center",
          color: "#2A2177",
          mb: 2,
          "@media (max-width: 1200px)": {
            fontSize: "2.5rem",
          },
          "@media (max-width: 780px)": {
            fontSize: "2rem",
          },
        }}
      >
        Real Impact of a Product-Agnostic Approach
      </Typography>
      <Typography
        sx={{
          fontSize: "18px",
          fontWeight: "regular",
          textAlign: "center",
          color: "#666666",
          lineHeight: 1.7,
          mb: 5,
          "@media (max-width: 1200px)": {
            fontSize: "16px",
          },
        }}
      >
        Let us guide you with a completely unbiased approach.
      </Typography>

      {/* Icons Grid */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 3,
          mb: 4,
          "@media (max-width: 1200px)": {
            flexWrap: "wrap",
          },
        }}
      >
        {/* Faster Decision-Making */}
        <Box
          sx={{
            textAlign: "center",
            width: "300px",
            backgroundColor: "#FFFFFF",
            borderRadius: "24px",
            py: "48px",
            px: "24px",
            boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
            ...gradientHoverSx,
            "@media (max-width: 1200px)": {
              width: "348px",
            },
            "@media (max-width: 780px)": {
              width: "100%",
            },
          }}
        >
          <GradientIcon
            icon={ElectricBoltOutlinedIcon}
            size={40}
            bordered
            boxSize={80}
            borderRadius={24}
            borderWidth={2}
            angle="90"
            sx={{ margin: "0 auto 16px" }}
          />
          <Typography
            sx={{
              fontSize: "1.25rem",
              fontWeight: 700,
              mb: 1,
              color: "#000000",
            }}
          >
            Faster Decision-Making
          </Typography>
          <Typography
            sx={{
              fontSize: "1rem",
              fontWeight: 400,
              color: "#666666",
              lineHeight: 1.5,
            }}
          >
            Make the right DAM choices quicker.
          </Typography>
        </Box>

        {/* Better Cross-Team Alignment */}
        <Box
          sx={{
            textAlign: "center",
            width: "300px",
            backgroundColor: "#FFFFFF",
            borderRadius: "24px",
            py: "48px",
            px: "24px",
            boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
            ...gradientHoverSx,
            "@media (max-width: 1200px)": {
              width: "348px",
            },
            "@media (max-width: 780px)": {
              width: "100%",
            },
          }}
        >
          <GradientIcon
            icon={ReduceCapacityOutlinedIcon}
            size={40}
            bordered
            boxSize={80}
            borderRadius={24}
            borderWidth={2}
            angle="90"
            sx={{ margin: "0 auto 16px" }}
          />
          <Typography
            sx={{
              fontSize: "1.25rem",
              fontWeight: 700,
              mb: 1,
              color: "#000000",
            }}
          >
            Better Cross-Team Alignment
          </Typography>
          <Typography
            sx={{
              fontSize: "1rem",
              fontWeight: 400,
              color: "#666666",
              lineHeight: 1.5,
            }}
          >
            Improves collaboration and consistency.
          </Typography>
        </Box>

        {/* Stronger Governance */}
        <Box
          sx={{
            textAlign: "center",
            width: "300px",
            backgroundColor: "#FFFFFF",
            borderRadius: "24px",
            py: "48px",
            px: "24px",
            boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
            ...gradientHoverSx,
            "@media (max-width: 1200px)": {
              width: "348px",
            },
            "@media (max-width: 780px)": {
              width: "100%",
            },
          }}
        >
          <GradientIcon
            icon={VerifiedUserOutlinedIcon}
            size={40}
            bordered
            boxSize={80}
            borderRadius={24}
            borderWidth={2}
            angle="90"
            sx={{ margin: "0 auto 16px" }}
          />
          <Typography
            sx={{
              fontSize: "1.25rem",
              fontWeight: 700,
              mb: 1,
              color: "#000000",
            }}
          >
            Stronger Governance
          </Typography>
          <Typography
            sx={{
              fontSize: "1rem",
              fontWeight: 400,
              color: "#666666",
              lineHeight: 1.5,
            }}
          >
            Ensures security and control over assets.
          </Typography>
        </Box>

        {/* Higher ROI */}
        <Box
          sx={{
            textAlign: "center",
            width: "300px",
            backgroundColor: "#FFFFFF",
            borderRadius: "24px",
            py: "48px",
            px: "24px",
            boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
            ...gradientHoverSx,
            "@media (max-width: 1200px)": {
              width: "348px",
            },
            "@media (max-width: 780px)": {
              width: "100%",
            },
          }}
        >
          <GradientIcon
            icon={ShowChartOutlinedIcon}
            size={40}
            bordered
            boxSize={80}
            borderRadius={24}
            borderWidth={2}
            angle="90"
            sx={{ margin: "0 auto 16px" }}
          />
          <Typography
            sx={{
              fontSize: "1.25rem",
              fontWeight: 700,
              mb: 1,
              color: "#000000",
            }}
          >
            Higher ROI
          </Typography>
          <Typography
            sx={{
              fontSize: "1rem",
              fontWeight: 400,
              color: "#666666",
              lineHeight: 1.5,
            }}
          >
            Maximize the value of your investment.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default RealImpact;
